import { daw } from "@highnote/daw/src";
import { AUDIO_QUALITY, FileEntity } from "@highnote/server/src/core/entities";
import { highnote } from "@highnote/server/src/sdk";
import { isFilePlayable } from "@highnote/server/src/core/shared-util";
import { ViewingSpaceId } from "App/modules/viewingSpace";

/**
 * `spaceId` is the VIEWING space — the space this file is being played FROM,
 * carried onto the queue at enqueue time, never read from the URL here (the
 * queue outlives the route it was built from).
 *
 * Note the `catch`: a denial from the server is swallowed and returns
 * `undefined`, which the player renders as `0%` with a warning icon and
 * NOTHING in the console. That silence is why a borrowing-only member's
 * playback failed with no diagnosable signal before this argument existed.
 */
const getSourceFromFile = async (
  file: FileEntity,
  quality: AUDIO_QUALITY,
  spaceId?: ViewingSpaceId,
) => {
  if (!file) return undefined;
  try {
    const url = await highnote.getFileUrl({
      id: file.id,
      asProcessedAudio: true,
      quality,
      ...(spaceId ? { spaceId } : {}),
    });
    return url;
  } catch (e) {
    return undefined;
  }
};

const PROCESSING_SOURCE = "/public/empty.wav";
export const loadFileIntoDAW = async (
  id: string,
  file: FileEntity,
  quality: AUDIO_QUALITY,
  startTime?: number,
  muted?: boolean,
  // The VIEWING space, from the queued track's own provenance. See
  // getSourceFromFile above.
  spaceId?: ViewingSpaceId,
) => {
  const dawTrack = daw.getTrack(id);

  let source: string;
  let sourceId = `${file.id}-${quality}`;
  let duration = 0;
  let fetchFromStorage = true;

  if (file.processingErrorV3) {
    sourceId = `${sourceId}-error`;
    fetchFromStorage = false;
  } else if (!isFilePlayable(file)) {
    sourceId = `${sourceId}-processing`;
    source = PROCESSING_SOURCE;
    duration = 1;
    fetchFromStorage = false;
  }

  // If this track has already been loaded, just use the old one.
  if (
    dawTrack &&
    dawTrack.sourceId === sourceId &&
    (startTime === undefined || dawTrack.startTime === startTime)
  ) {
    if (muted) daw.muteTrack(id);
    else daw.unmuteTrack(id);
    return;
  }

  daw.removeTrack(id);

  if (fetchFromStorage) {
    source = await getSourceFromFile(file, quality, spaceId);
    if (source) {
      duration = file.metadata.duration;
    }
  }

  daw.addTrack({
    id,
    source,
    sourceId,
    startTime: startTime || 0,
    duration,
    ...(muted ? { volume: 0 } : {}),
  });
};
